/**
 * Left-sliding PDF preview drawer.
 *
 * Fetches the paper file from ai_training as a Blob (via the axios
 * client, so the trainer JWT rides on it), turns it into an object
 * URL, and renders it in an <iframe>. Object URLs are revoked when
 * the drawer closes or the paper changes so we don't leak memory.
 *
 * A native <iframe> is enough — every current browser has a built-in
 * PDF renderer and this avoids pulling PDF.js as a heavy dependency.
 */
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  AlertCircle, Download, ExternalLink, Loader2, X,
} from 'lucide-react';
import { aiTrainerClient } from '@/lib/aiTrainerClient';
import type { Paper } from './types';

interface Props {
  paper: Paper | null;
  onClose: () => void;
}

export const PdfPreviewDrawer: React.FC<Props> = ({ paper, onClose }) => {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState<string | null>(null);

  useEffect(() => {
    if (!paper) { setBlobUrl(null); setError(null); return; }
    let alive  = true;
    let localUrl: string | null = null;

    setLoading(true);
    setError(null);
    setBlobUrl(null);

    aiTrainerClient
      .get(`/papers/${paper.id}/file`, { responseType: 'blob' })
      .then((r) => {
        if (!alive) return;
        localUrl = URL.createObjectURL(r.data);
        setBlobUrl(localUrl);
      })
      .catch((err) => {
        if (!alive) return;
        const msg = err?.response?.data?.detail
          || err?.response?.status
          || err?.message
          || 'Could not load preview';
        setError(String(msg));
      })
      .finally(() => alive && setLoading(false));

    return () => {
      alive = false;
      if (localUrl) URL.revokeObjectURL(localUrl);
    };
  }, [paper?.id]);

  // ESC-to-close for keyboard users.
  useEffect(() => {
    if (!paper) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [paper, onClose]);

  if (!paper || typeof document === 'undefined') return null;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Preview ${paper.file_name}`}
      className="fixed inset-0 z-[10001] flex bg-slate-900/50 backdrop-blur-sm animate-[pdvFade_.2s_ease]"
      onClick={onClose}
    >
      <aside
        onClick={(e) => e.stopPropagation()}
        className="flex h-full w-full max-w-[880px] flex-col bg-slate-100 shadow-[20px_0_60px_rgba(0,0,0,0.25)] animate-[pdvSlideInLeft_.28s_ease]"
        style={{ fontFamily: "'Afacad Flux', system-ui, sans-serif" }}
      >
        {/* Head */}
        <div className="flex items-center justify-between gap-3 border-b border-slate-200 bg-white px-5 py-3">
          <div className="min-w-0">
            <p className="m-0 text-[10.5px] font-extrabold uppercase tracking-[.12em] text-[#0e9172]">
              Past-paper preview
            </p>
            <h3
              className="m-0 mt-0.5 truncate text-[20px] leading-tight text-slate-900"
              style={{ fontFamily: "'Instrument Serif', serif" }}
            >
              {paper.course_code
                ? <>{paper.course_code} <span className="text-slate-400">·</span> {paper.year || '—'}</>
                : paper.file_name}
            </h3>
            <p className="m-0 mt-0.5 truncate text-[11.5px] text-slate-500">
              {paper.file_name}
              {paper.paper_type && <> · {paper.paper_type}</>}
              {paper.page_count != null && <> · {paper.page_count} page{paper.page_count === 1 ? '' : 's'}</>}
            </p>
          </div>
          <div className="flex items-center gap-1">
            {blobUrl && (
              <>
                <a
                  href={blobUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Open in a new tab"
                  className="flex h-8 w-8 items-center justify-center rounded-lg bg-slate-100 text-slate-700 hover:bg-slate-200"
                >
                  <ExternalLink size={14} />
                </a>
                <a
                  href={blobUrl}
                  download={paper.file_name}
                  title="Download PDF"
                  className="flex h-8 w-8 items-center justify-center rounded-lg bg-slate-100 text-slate-700 hover:bg-slate-200"
                >
                  <Download size={14} />
                </a>
              </>
            )}
            <button
              type="button"
              onClick={onClose}
              title="Close (Esc)"
              className="flex h-8 w-8 items-center justify-center rounded-lg bg-slate-100 text-slate-900 hover:bg-slate-200"
            >
              <X size={16} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="relative flex-1 overflow-hidden bg-[#3a3a3a]">
          {loading && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-slate-100">
              <Loader2 size={26} className="animate-spin text-[#5ecdb0]" />
              <p className="m-0 text-[12.5px]">Loading PDF…</p>
            </div>
          )}
          {error && !loading && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 px-4 text-center text-slate-100">
              <AlertCircle size={26} className="text-[#f59e0b]" />
              <p className="m-0 text-[13px] font-semibold">Preview unavailable</p>
              <p className="m-0 max-w-[400px] text-[11.5px] text-slate-300">
                {error}
              </p>
            </div>
          )}
          {blobUrl && (
            <iframe
              key={paper.id}
              src={blobUrl}
              title={paper.file_name}
              className="h-full w-full border-0"
            />
          )}
        </div>

        <style>{`
          @keyframes pdvFade { from { opacity: 0; } to { opacity: 1; } }
          @keyframes pdvSlideInLeft {
            from { transform: translateX(-100%); opacity: .95; }
            to   { transform: translateX(0);     opacity: 1; }
          }
        `}</style>
      </aside>
    </div>,
    document.body,
  );
};
